import { Inject, Injectable } from '@angular/core';
import { Benachrichtigung, BenachrichtigungInfo } from './classes/benachrichtigung';
import { Observable, Subject } from 'rxjs';
import { API_BASE_URL_SERVICE } from '../kiene-core.config';
import { HttpParams } from '@angular/common/http';
import { KieneApi, KieneBackendApiService } from '../services/backend-api/kiene-backend-api.service';
import { BenachrichtigungClickEvent } from './classes/benachrichtigung-click-event';
import { MessageService } from '../services/message-service/message.service';
import { ConfirmDialogInput, ConfirmDialogResult } from '../components/dialogs/confirm-dialog/confirm-dialog';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '../components/dialogs/confirm-dialog/confirm-dialog.component';

@Injectable({ providedIn: 'root' })
export class BenachrichtigungenService {
    benachrichtigungSubject: Subject<Benachrichtigung[]> = new Subject();
    benachrichtigungClickEventSubject = new Subject<BenachrichtigungClickEvent>();
    reloadSubject = new Subject<boolean>();
    /** Verhindert, dass dieselbe Popup-Nachricht in einer Session erneut aufpoppt. */
    private readonly dismissedPopupBbIds = new Set<number>();
    private popupInFlight = false;

    constructor(
        private readonly api: KieneBackendApiService,
        @Inject(API_BASE_URL_SERVICE) private readonly baseUrl: string,
        private readonly messageService: MessageService,
        private readonly dialog: MatDialog,
    ) {}

    markBenachrichtigungAsRead(benachrichtigung: Benachrichtigung): Observable<any> {
        return this.api.updateGeneric(`${this.baseUrl}benachrichtigung/update_gelesen.php`, {
            bb_id: Number(benachrichtigung.bb_id),
        });
    }

    getBenachrichtigungen(filter?: { search?: any; gelesen?: number; offset?: number; limit?: number }) {
        let params = new HttpParams();
        params = params.append('orderby', 'erstellt desc');
        if (filter?.search) {
            params = params.append('search', filter.search);
        }
        if (filter?.gelesen >= 0) {
            params = params.append('gelesen', filter.gelesen);
        }
        if (filter?.offset >= 0) {
            params = params.append('offset', filter.offset);
        }
        if (filter?.limit) {
            params = params.append('limit', filter.limit);
        }
        return this.api.getAll(`${this.baseUrl}benachrichtigung/read.php`, params);
    }

    markAllBenachrichtigungAsRead(): Observable<any> {
        return this.api.updateGeneric(this.baseUrl + 'benachrichtigung/update_gelesen.php', { alle: 1 });
    }

    watchReload() {
        return this.reloadSubject.asObservable();
    }

    triggerReload() {
        return this.reloadSubject.next(true);
    }

    getUngeleseneBenachrichtigungenCount(): Observable<BenachrichtigungInfo> {
        return this.api.get(`${this.baseUrl}benachrichtigung/read_ungelesen.php`);
    }

    fireBenachrichtiungClickEvent(event: BenachrichtigungClickEvent) {
        this.benachrichtigungClickEventSubject.next(event);
    }

    watchBenachrichtigungClickEvent(): Observable<BenachrichtigungClickEvent> {
        return this.benachrichtigungClickEventSubject.asObservable();
    }

    showPopup(): Observable<boolean> {
        const subject = new Subject<boolean>();
        if (this.popupInFlight) {
            subject.next(false);
            return subject;
        }

        const params = new HttpParams().set('popup', 1).set('gelesen', 0);
        this.api.getAll(this.baseUrl + 'benachrichtigung/read.php', params).subscribe({
            next: (records: KieneApi) => {
                if (records?.records?.length > 0 && this.dialog.openDialogs?.length == 0) {
                    const benachrichtigung: Benachrichtigung = records.records[0];
                    const bbId = Number(benachrichtigung.bb_id);
                    if (!bbId || this.dismissedPopupBbIds.has(bbId)) {
                        subject.next(false);
                        return;
                    }

                    this.popupInFlight = true;
                    this.dismissedPopupBbIds.add(bbId);

                    const input = new ConfirmDialogInput();
                    input.headline = benachrichtigung.titel;
                    input.text = benachrichtigung.txt;
                    input.okButton = 'OK';
                    input.showCancel = false;

                    this.dialog
                        .open(ConfirmDialogComponent, { data: input, disableClose: true })
                        .afterClosed()
                        .subscribe({
                            next: (result: ConfirmDialogResult) => {
                                this.popupInFlight = false;
                                if (result?.confirmed) {
                                    this.markBenachrichtigungAsRead(benachrichtigung).subscribe({
                                        next: () => {
                                            this.triggerReload();
                                            subject.next(true);
                                        },
                                        error: err => {
                                            this.messageService.errorMessage('Fehler!', err);
                                            subject.next(false);
                                        },
                                    });
                                } else {
                                    subject.next(false);
                                }
                            },
                        });
                } else {
                    subject.next(false);
                }
            },
            error: err => {
                this.popupInFlight = false;
                this.messageService.errorMessage('Fehler!', err);
                subject.next(false);
            },
        });
        return subject;
    }
}
